import { bind, Component, h, tag } from "omi";

import { ControlStatus } from "@/components/validations/models.ts";
import { AuthEvents } from "@/components/nw-auth-manager";
import { COMMUNITY_LINK_WITH_ERROR_UTM } from "@/globals/links.ts";
import { NWBanner, NWButton, NWIcon, NWTooltip } from "@/components";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";

export type Props = {
  status: ControlStatus;
  isDoneEnabled: boolean;
  onDone: () => void;
  onMarkForReview: () => void;
  isLoadingDone: boolean;
  isLoadingReview: boolean;
};

@tag("nw-validation-controls")
export class NWValidationControls extends Component<Props> {
  static css = [tailwind, `:host { width: fit; }`];

  static propTypes = {
    isDoneEnabled: Boolean,
    onDone: Function,
    status: String,
    onMarkForReview: Function,
    isLoadingDone: Boolean,
    isLoadingReview: Boolean,
  };

  @bind
  renderReviewOrBanner(status: ControlStatus) {
    if (status === ControlStatus.ERROR) {
      return (
        <NWBanner type="error">
          Whoops! This one's on us 🙏 Try it again, or{" "}
          <a
            class="font-normal underline text-secondary hover:text-secondary-hover transition-colors"
            target="_blank"
            href={COMMUNITY_LINK_WITH_ERROR_UTM}
          >
            let us know.
          </a>
        </NWBanner>
      );
    }

    if (status === ControlStatus.INCORRECT) {
      return <NWBanner type="error">Hmmm... 🤔 Your answer is incorrect, give it another go.</NWBanner>;
    }

    if (status === ControlStatus.REVIEW) {
      return (
        <div class="animate-dropdownEntry">
          <NWBanner type="neutral" iconClass="text-success-400">
            Marked for review! Remember to mark this as complete once you revisit it.
          </NWBanner>
        </div>
      );
    }

    return (
      <NWButton
        onClick={this.props.onMarkForReview}
        loading={this.props.isLoadingReview}
        type="text"
        size="full"
        icon="flip-backwards"
      >
        Return to later
      </NWButton>
    );
  }

  private triggerLogin() {
    const loginEvent = new Event(AuthEvents.LoginClicked);
    dispatchEvent(loginEvent);
  }

  render({ isLoadingDone, status = ControlStatus.INCOMPLETE }: Props) {
    const isLoggedIn = Store.user.isLoggedIn.value;

    return (
      <div class="flex flex-col items-center justify-center">
        <div class="w-full max-w-sm flex justify-center space-y-3 flex-col">
          {status === ControlStatus.COMPLETE && (
            <div class="animate-slideDownEntry flex items-center justify-center space-x-4">
              <NWIcon name="green-check-circle" width={32} height={32}></NWIcon>
              <p class="text-green-600 font-semibold">Task Completed</p>
            </div>
          )}

          {status !== ControlStatus.COMPLETE && (
            <div class="animate-fadeInFast">
              <NWTooltip
                content={
                  Store.user.isPro()
                    ? "Go for it! Edit all you want—you're a Pro ⭐️"
                    : "Careful! Once selected, you can't edit your answer."
                }
                placement="top"
                shouldShow={this.props.isDoneEnabled}
              >
                <NWButton
                  disabled={isLoggedIn ? !this.props.isDoneEnabled : false}
                  onClick={isLoggedIn ? this.props.onDone : this.triggerLogin}
                  type={isLoggedIn ? "secondary-color" : "secondary"}
                  size="full"
                  icon={this.props.isDoneEnabled ? "tick" : "gray-check"}
                  loading={isLoadingDone}
                >
                  {this.props.isDoneEnabled ? "Done" : "Tasks still to complete"}
                </NWButton>
              </NWTooltip>
              <div class="mt-3">{this.renderReviewOrBanner(status)}</div>
            </div>
          )}
        </div>
      </div>
    );
  }
}
